import React, { useMemo, useState } from 'react';

import { CSVLink } from 'react-csv';

import useQuery from '../../../helpers/JIFI/useQuery';

import SupplyChainNav from '../../../components/supply-chain/nav.js';

import { productOf } from './sku-family';
import RecalculateCogsDialog from './recalculate-dialog';
import RateBreakdownTooltip from './rate-breakdown-tooltip';
import { Col } from '@shadcn/components/ui/col.jsx';
import { Card, CardContent as CardBody } from '@shadcn/components/ui/card.jsx';
import { Button } from '@shadcn/components/ui/button.jsx';
import { Input } from '@shadcn/components/ui/input.jsx';
import { TableSkeleton } from '@shadcn/components/ui/table.jsx';

const REGIONS = ['AU', 'US', 'UK', 'EU'];
const fmtDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '-');
const fmtMoney = (minor) => (minor != null ? (minor / 100).toFixed(2) : '-');
const mean = (values) =>
  values.length ? Math.round(values.reduce((s, v) => s + v, 0) / values.length) : null;
// The window's days in the rate's market, as the API names them. Its instants' UTC dates
// are a day out west of UTC; only a rate stamped before the days were recorded (a window
// read in UTC) falls back to them.
const windowFrom = (r) => r.windowFromDate || fmtDate(r.windowStart);
const windowTo = (r) => r.windowToDate || fmtDate(r.windowEnd);
// A rate the recalculated window held no container for: every figure reads 0 and the
// window reads N/A. Strictly 0 — absent means the row was never stamped by a recompute.
const isUnbacked = (r) => r.containerCount === 0;
const fmtWindow = (r) => (isUnbacked(r) ? 'N/A' : `${windowFrom(r)} → ${windowTo(r)}`);
// The row reads 0.00, but its stock is valued at the last rate, as holding and valuation do.
const UNBACKED_VALUE_NOTE = 'Valued at the last rate; no container landed in this window.';
// A product's value leaves out SKUs with no rate, while its SOH counts them; say how many.
const unratedNote = (units) => (units ? `Excludes ${units} unrated unit${units === 1 ? '' : 's'}` : undefined);

// COGS landed-cost rates per SKU per region, grouped by product (avg COGS),
// with window + container provenance per SKU.
const CogsRateList = () => {
  const dateString = new Date().toDateString().replace(/ /g, '-');
  const [region, setRegion] = useState('AU');
  const [skuInput, setSkuInput] = useState('');
  const [sku, setSku] = useState('');

  // Nothing recomputes COGS automatically, so rates go stale after container costs
  // change. Recalculating is windowed, and the window it ran over is what the table's
  // Window column then shows.
  const [recalculateOpen, setRecalculateOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);

  const [rows, rowsState] = useQuery({
    url: `/cogs/rates`,
    queryData: { region, sku },
    refresh,
  });

  // Group by product. Rebuilt only when the rates load, not on each keystroke in the SKU filter.
  const groups = useMemo(() => {
    const groupsMap = {};
    for (const r of Array.isArray(rows) ? rows : []) {
      const p = productOf(r.sku);
      (groupsMap[p] = groupsMap[p] || []).push(r);
    }
    return Object.keys(groupsMap)
      .sort()
      .map((product) => {
        const items = groupsMap[product].sort((a, b) => a.sku.localeCompare(b.sku));
        // Freight is derived (rate - FOB), so both means must cover the same SKUs or the
        // unsplit SKUs' rates land in freight. Show the split only when every rate has one.
        // Unbacked SKUs are not real zeros, so averaging them in would drag the product's
        // landed cost down to a figure nothing supports.
        const rated = items.filter((i) => !isUnbacked(i) && i.rateMinorUnits != null);
        const allUnbacked = items.every(isUnbacked);
        const avgRate = allUnbacked ? 0 : mean(rated.map((i) => i.rateMinorUnits));
        const splittable = rated.length > 0 && rated.every((i) => i.fobRateMinorUnits != null);
        const avgFob = allUnbacked ? 0 : splittable ? mean(rated.map((i) => i.fobRateMinorUnits)) : null;
        const avgFreight = allUnbacked ? 0 : splittable ? avgRate - avgFob : null;
        // Stock is a total, not a profile, so the header sums its SKUs (unbacked ones included,
        // as they are still valued at their last rate). Unrated SKUs add nothing, as in valuation.
        const values = items.map((i) => i.stockValueMinorUnits).filter((v) => v != null);
        const sohUnits = items.reduce((s, i) => s + (i.sohUnits ?? 0), 0);
        const stockValue = values.length ? values.reduce((s, v) => s + v, 0) : null;
        const unratedUnits = items.reduce((s, i) => s + (i.stockValueMinorUnits == null ? i.sohUnits ?? 0 : 0), 0);
        return { product, items, avgRate, avgFob, avgFreight, sohUnits, stockValue, unratedUnits };
      });
  }, [rows]);

  const csvData = useMemo(
    () => [
      [
        'Product', 'SKU', 'Region', 'FOB', 'Freight', 'Rate',
        'Window Start', 'Window End', 'Containers', 'SOH', 'Stock Value',
      ],
      ...groups.flatMap((g) => [
        [
          g.product, '', region, fmtMoney(g.avgFob), fmtMoney(g.avgFreight), fmtMoney(g.avgRate),
          '', '', '', g.sohUnits, fmtMoney(g.stockValue),
        ],
        ...g.items.map((r) => [
          '',
          r.sku,
          r.region,
          fmtMoney(isUnbacked(r) ? 0 : r.fobRateMinorUnits),
          fmtMoney(isUnbacked(r) ? 0 : r.freightRateMinorUnits),
          fmtMoney(isUnbacked(r) ? 0 : r.rateMinorUnits),
          ...(isUnbacked(r) ? ['N/A', 'N/A'] : [windowFrom(r), windowTo(r)]),
          r.containerCount ?? 0,
          r.sohUnits ?? 0,
          fmtMoney(r.stockValueMinorUnits),
        ]),
      ]),
    ],
    [groups, region],
  );

  return (
    <SupplyChainNav>
      <div>
        <Col className="w-full">
          <Card className="pt-6 mb-10">
            <CardBody>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <h4 className="mb-3">COGS Rates</h4>
                <div className="flex gap-2 items-start">
                  <Button
                    type="button"
                    variant="secondary"
                    className="btn-md btn-rounded"
                    onClick={() => setRecalculateOpen(true)}
                    title="Recompute COGS for the containers received in a window"
                  >
                    Recalculate COGS
                  </Button>
                  <CSVLink
                    style={{ marginBottom: '1.25rem' }}
                    data={csvData}
                    filename={`COGS-RATES-${region}-${dateString}.csv`}
                  >
                    <Button type="button" variant="secondary" className="btn-md btn-rounded">
                      CSV
                    </Button>
                  </CSVLink>
                </div>
              </div>

              <div className="flex gap-4 mb-3 items-end flex-wrap justify-between">
                <div>
                  <label className="control-label">MARKET</label>
                  <select
                    className="form-control"
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                  >
                    {REGIONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex-1" style={{ minWidth: '18rem' }}>
                  <label className="control-label">FILTER SKU</label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      className="form-control"
                      placeholder="Filter by SKU…"
                      value={skuInput}
                      onChange={(e) => setSkuInput(e.target.value)}
                      onKeyDown={(e) => (e.key === 'Enter' ? setSku(skuInput.trim()) : null)}
                    />
                    <Button type="button" variant="primary" onClick={() => setSku(skuInput.trim())}>
                      Apply
                    </Button>
                  </div>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-centered table-nowrap">
                  <thead>
                    {/* Every column is pinned, so nothing shifts when the longest SKU in
                        the data changes (e.g. switching market). 48ch is double the
                        longest SKU in the catalogue today (JETTE-SUEDE-CRSBDY61-WHI, 24). */}
                    <tr>
                      <th style={{ width: '48ch' }}>Product / SKU</th>
                      <th className="text-center" style={{ width: '7rem' }}>FOB</th>
                      <th className="text-center" style={{ width: '7rem' }}>Freight</th>
                      <th className="text-center" style={{ width: '7rem' }}>Rate</th>
                      <th className="text-center" style={{ width: '15rem' }}>Window</th>
                      <th className="text-center" style={{ width: '8rem' }}>Containers</th>
                      <th className="text-center" style={{ width: '7rem' }} title="Today, every physical bucket">
                        SOH
                      </th>
                      <th className="text-center" style={{ width: '9rem' }}>Stock Value</th>
                    </tr>
                  </thead>
                  {rowsState === 'success' ? (
                    <tbody>
                      {groups.length > 0 ? (
                        groups.map((g) => (
                          <React.Fragment key={g.product}>
                            <tr style={{ background: '#f6f6f6' }}>
                              <td className="font-weight-bold">
                                {g.product}{' '}
                                <span className="text-muted-foreground">({g.items.length})</span>
                              </td>
                              <td className="text-center font-weight-bold">{fmtMoney(g.avgFob)}</td>
                              <td className="text-center font-weight-bold">{fmtMoney(g.avgFreight)}</td>
                              <td className="text-center font-weight-bold">{fmtMoney(g.avgRate)}</td>
                              <td className="text-center" />
                              <td className="text-center" />
                              <td className="text-center font-weight-bold">{g.sohUnits}</td>
                              <td className="text-center font-weight-bold" title={unratedNote(g.unratedUnits)}>
                                {fmtMoney(g.stockValue)}
                                {g.unratedUnits ? '*' : ''}
                              </td>
                            </tr>
                            {g.items.map((r, key) => (
                              <tr key={`${g.product}-${r.sku}-${key}`}>
                                <td style={{ paddingLeft: '2rem' }}>
                                  <RateBreakdownTooltip sku={r.sku} region={region} ratesVersion={refresh} />
                                </td>
                                <td className="text-center">
                                  {fmtMoney(isUnbacked(r) ? 0 : r.fobRateMinorUnits)}
                                </td>
                                <td className="text-center">
                                  {fmtMoney(isUnbacked(r) ? 0 : r.freightRateMinorUnits)}
                                </td>
                                <td className="text-center">
                                  {fmtMoney(isUnbacked(r) ? 0 : r.rateMinorUnits)}
                                </td>
                                <td className="text-center">{fmtWindow(r)}</td>
                                <td className="text-center">{r.containerCount ?? 0}</td>
                                <td className="text-center">{r.sohUnits ?? 0}</td>
                                <td
                                  className="text-center"
                                  title={isUnbacked(r) && r.stockValueMinorUnits ? UNBACKED_VALUE_NOTE : undefined}
                                >
                                  {fmtMoney(r.stockValueMinorUnits)}
                                  {isUnbacked(r) && r.stockValueMinorUnits ? '*' : ''}
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={8} className="text-center">
                            No COGS rates computed yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  ) : (
                    <TableSkeleton columns={8} perPage={10} centerIndex={[1, 2, 3, 4, 5, 6, 7]} />
                  )}
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </div>

      <RecalculateCogsDialog
        open={recalculateOpen}
        onClose={() => setRecalculateOpen(false)}
        onRecalculated={() => setRefresh((n) => n + 1)}
      />
    </SupplyChainNav>
  );
};

export default CogsRateList;
